import type { Dispatch, ReactNode, SetStateAction } from "react"

import { addDays, format, isBefore, startOfDay } from "date-fns"
import { CalendarIcon, Loader2, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import {
  formatDayLong,
  formatDayMonth,
  formatDayMonthYear,
  formatDayShortYear,
} from "@/lib/dateFormats"
import { useOperatorAllocations, useOperators } from "@/hooks/useCollections"
import { formatAllocationDates } from "@/lib/operatorAllocations"
import { isSelectableBoundary } from "@/lib/planLock"
import { dayOfWeekOf } from "@/lib/plannedRouteMapping"
import { cn } from "@/lib/utils"
import {
  type DayOfWeek,
  type PlannedRoute,
  ROUTE_CATEGORIES,
  ROUTE_KIND_LABELS,
  ROUTE_KINDS,
  type RouteCategory,
  type RouteKind,
  type RoutePlanned,
} from "@/types/collections"

import PlanLineageSection from "../PlanLineageSection"
import SubcontractorsButton from "../SubcontractorsButton"

import type { RouteFormData } from "./types"

/** Fallback until the operator list loads. */
const DEFAULT_OPERATORS = ["HIVED", "Oaklands"]

const dayOptions: { value: DayOfWeek; label: string }[] = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
]

export function PlanningRouteDialog({
  open,
  onOpenChange,
  isCreatingNewRoute,
  route,
  selectedDate,
  minDate,
  form,
  setForm,
  routeCodeClash,
  isRenamingRoute,
  effectiveRenameFrom,
  onRenameFromChange,
  vehicleTypeOptions,
  isSaving,
  onSave,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  isCreatingNewRoute: boolean
  route: PlannedRoute | null
  selectedDate: Date
  minDate: Date
  form: RouteFormData
  setForm: Dispatch<SetStateAction<RouteFormData>>
  routeCodeClash: RoutePlanned | undefined
  isRenamingRoute: boolean
  effectiveRenameFrom: Date
  onRenameFromChange: (date: Date) => void
  vehicleTypeOptions: string[]
  isSaving: boolean
  onSave: () => void
  children?: ReactNode
}) {
  // The route's own operator is always an option, even if the list lacks it or hasn't loaded.
  const { data: operatorsData } = useOperators(open)
  const listed = operatorsData?.data?.map((operator) => operator.name) ?? []
  const known = listed.length > 0 ? listed : DEFAULT_OPERATORS
  const operatorOptions =
    form.operator && !known.includes(form.operator) ? [form.operator, ...known] : known
  const plannedRouteId = !isCreatingNewRoute ? route?.id : undefined
  const { data: handedOver = [] } = useOperatorAllocations(
    plannedRouteId,
    open && !!plannedRouteId,
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isCreatingNewRoute ? "Create New Route" : "Edit Route"}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1">
              <p className="text-sm text-palette-jungleGreen500">
                {isCreatingNewRoute
                  ? formatDayLong(selectedDate)
                  : route?.code}
              </p>
              <p className="text-xs text-palette-jungleGreen500">
                {isCreatingNewRoute
                  ? "The route will be created and instances generated from this date."
                  : "Saving will update the planned route and all route instances from the day you're editing onwards."}
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <section className="rounded-xl border border-palette-jungleGreen200 bg-palette-jungleGreen000/50 p-4 space-y-4">
            <h3 className="text-xs font-semibold text-palette-jungleGreen500 uppercase tracking-wide">
              Route details
            </h3>
            <div className="grid gap-3">
              <div className="grid gap-2">
                <Label
                  htmlFor="routeName"
                  className="text-xs font-medium text-palette-jungleGreen700"
                >
                  Route code *
                </Label>
                {/* Labelled "code": it prefixes every daily route code. The form key is still `name`. */}
                <Input
                  id="routeName"
                  placeholder="e.g. SC-01 or London Morning Collection"
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="h-9 border-palette-jungleGreen200 bg-white text-sm"
                />
                {routeCodeClash ? (
                  <p className="text-xs text-red-600">
                    {routeCodeClash.code} is already used by another route
                    {routeCodeClash.depot ? ` (${routeCodeClash.depot})` : ""}. Codes must be
                    unique.
                  </p>
                ) : isRenamingRoute ? (
                  <div className="grid gap-2">
                    <Label className="text-xs text-palette-jungleGreen600">
                      Rename takes effect from
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 w-full justify-start border-palette-jungleGreen200 bg-white text-sm"
                        >
                          <CalendarIcon
                            size={14}
                            className="mr-2 text-palette-jungleGreen500"
                          />
                          {formatDayShortYear(effectiveRenameFrom)}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent variant="plain" align="start">
                        <Calendar
                          mode="single"
                          selected={effectiveRenameFrom}
                          onSelect={(date) => date && onRenameFromChange(date)}
                          // Mondays only, outside the locked week: a mid-week switch would give one week two codes.
                          disabled={(date) => !isSelectableBoundary(date, new Date())}
                          className="bg-white"
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs font-medium text-amber-700">
                      {route?.code} → {form.name.trim()} from{" "}
                      {formatDayMonth(effectiveRenameFrom)}
                    </p>
                    <p className="text-xs text-palette-jungleGreen500">
                      The plan is locked in to{" "}
                      {formatDayMonth(addDays(effectiveRenameFrom, -1))}, so earlier daily
                      routes keep {route?.code}.
                    </p>
                  </div>
                ) : (
                  !isCreatingNewRoute && (
                    <p className="text-xs text-palette-jungleGreen500">
                      Used to build every daily route code, e.g.{" "}
                      {`[COL]${form.name.trim() || "CODE"}-20260825-1`}.
                    </p>
                  )
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label
                    htmlFor="category"
                    className="text-xs font-medium text-palette-jungleGreen700"
                  >
                    Category
                  </Label>
                  <Select
                    value={form.category}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        category: value as RouteCategory,
                      }))
                    }
                  >
                    <SelectTrigger className="h-9 text-sm bg-white border-palette-jungleGreen200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {ROUTE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="status"
                    className="text-xs font-medium text-palette-jungleGreen700"
                  >
                    Status
                  </Label>
                  <Select
                    value={form.status}
                    onValueChange={(value: "draft" | "live" | "cancelled") =>
                      setForm((prev) => ({ ...prev, status: value }))
                    }
                  >
                    <SelectTrigger className="h-9 text-sm bg-white border-palette-jungleGreen200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="live">Live</SelectItem>
                      {!isCreatingNewRoute && (
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label
                    htmlFor="operator"
                    className="text-xs font-medium text-palette-jungleGreen700"
                  >
                    Operator *
                  </Label>
                  <Select
                    value={form.operator}
                    onValueChange={(value) =>
                      setForm((prev) => ({ ...prev, operator: value }))
                    }
                  >
                    <SelectTrigger className="h-9 text-sm bg-white border-palette-jungleGreen200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {operatorOptions.map((op) => (
                        <SelectItem key={op} value={op}>
                          {op}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="vehicleType"
                    className="text-xs font-medium text-palette-jungleGreen700"
                  >
                    Vehicle type *
                  </Label>
                  <Select
                    value={form.vehicleType}
                    onValueChange={(value) =>
                      setForm((prev) => ({ ...prev, vehicleType: value }))
                    }
                  >
                    <SelectTrigger className="h-9 text-sm bg-white border-palette-jungleGreen200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {vehicleTypeOptions.map((vt) => (
                        <SelectItem key={vt} value={vt}>
                          {vt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {plannedRouteId && (
                <div className="flex items-center justify-between gap-3">
                  <p className="min-w-0 text-xs text-palette-jungleGreen500">
                    {handedOver
                      .map((a) => `${a.operator} ${formatAllocationDates(a)}`)
                      .join(" · ")}
                  </p>
                  <SubcontractorsButton routeId={plannedRouteId} className="shrink-0" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label
                    htmlFor="editDoubleManned"
                    className="text-xs font-medium text-palette-jungleGreen700"
                  >
                    Crew
                  </Label>
                  <div className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-palette-jungleGreen200 bg-white px-3 py-2 text-sm">
                    <span className="text-palette-jungleGreen900">Double-manned</span>
                    <Switch
                      id="editDoubleManned"
                      checked={form.crewSize > 1}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({ ...prev, crewSize: checked ? 2 : 1 }))
                      }
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="editRouteKind"
                    className="text-xs font-medium text-palette-jungleGreen700"
                  >
                    Kind
                  </Label>
                  <Select
                    value={form.routeKind}
                    onValueChange={(value) =>
                      setForm((prev) => ({ ...prev, routeKind: value as RouteKind }))
                    }
                  >
                    <SelectTrigger
                      id="editRouteKind"
                      className="h-9 text-sm bg-white border-palette-jungleGreen200"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {ROUTE_KINDS.map((kind) => (
                        <SelectItem key={kind} value={kind}>
                          {ROUTE_KIND_LABELS[kind]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-palette-jungleGreen200 bg-palette-jungleGreen000/50 p-4 space-y-4">
            <h3 className="text-xs font-semibold text-palette-jungleGreen500 uppercase tracking-wide">
              Schedule
            </h3>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="editRecurring"
                checked={form.isRecurring}
                onCheckedChange={(checked) => {
                  if (checked && isCreatingNewRoute) {
                    const dayOfWeek = dayOfWeekOf(selectedDate)
                    setForm((prev) => ({
                      ...prev,
                      isRecurring: true,
                      recurringDays: prev.recurringDays.includes(dayOfWeek)
                        ? prev.recurringDays
                        : [...prev.recurringDays, dayOfWeek],
                    }))
                  } else {
                    setForm((prev) => ({ ...prev, isRecurring: checked === true }))
                  }
                }}
              />
              <Label
                htmlFor="editRecurring"
                className="text-sm font-medium text-palette-jungleGreen800 cursor-pointer"
              >
                Recurring route
              </Label>
            </div>

            {form.isRecurring && (
              <div className="space-y-3 pl-6 border-l-2 border-amber-200 ml-1">
                <div className="grid gap-2">
                  <Label className="text-xs text-palette-jungleGreen600">
                    Repeat on days
                  </Label>
                  <div className="flex flex-wrap gap-1.5">
                    {dayOptions.map((day) => {
                      const isSelected = form.recurringDays.includes(day.value)
                      const isLocked =
                        isCreatingNewRoute && day.value === dayOfWeekOf(selectedDate)
                      return (
                        <label
                          key={day.value}
                          className={cn(
                            "px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors",
                            isSelected
                              ? "bg-amber-100 border-amber-300 text-amber-800"
                              : "bg-white border-palette-jungleGreen200 text-palette-jungleGreen600 hover:bg-palette-jungleGreen000",
                            isLocked && "cursor-not-allowed opacity-75",
                            !isLocked && "cursor-pointer",
                          )}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={isSelected}
                            disabled={isLocked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setForm((prev) => ({
                                  ...prev,
                                  recurringDays: [...prev.recurringDays, day.value],
                                }))
                              } else {
                                setForm((prev) => ({
                                  ...prev,
                                  recurringDays: prev.recurringDays.filter(
                                    (d) => d !== day.value,
                                  ),
                                }))
                              }
                            }}
                          />
                          {day.label.slice(0, 3)}
                          {isLocked && <span className="ml-0.5 text-[10px]">🔒</span>}
                        </label>
                      )
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-2">
                    <Label className="text-xs text-palette-jungleGreen600">
                      Effective from *
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 justify-start text-sm bg-white w-full border-palette-jungleGreen200"
                        >
                          <CalendarIcon
                            size={14}
                            className="mr-2 text-palette-jungleGreen500"
                          />
                          {form.effectiveFromDate
                            ? formatDayMonthYear(form.effectiveFromDate)
                            : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent variant="plain" align="start">
                        <Calendar
                          mode="single"
                          selected={form.effectiveFromDate}
                          onSelect={(date) =>
                            setForm((prev) => ({ ...prev, effectiveFromDate: date }))
                          }
                          disabled={(date) => isBefore(date, minDate)}
                          className="bg-white"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="grid gap-2">
                    <Label className="text-xs text-palette-jungleGreen600">
                      Effective to (optional)
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 justify-start text-sm bg-white w-full border-palette-jungleGreen200"
                        >
                          <CalendarIcon
                            size={14}
                            className="mr-2 text-palette-jungleGreen500"
                          />
                          {form.effectiveToDate
                            ? formatDayMonthYear(form.effectiveToDate)
                            : "No end date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent variant="plain" align="start">
                        <Calendar
                          mode="single"
                          selected={form.effectiveToDate}
                          onSelect={(date) =>
                            setForm((prev) => ({ ...prev, effectiveToDate: date }))
                          }
                          disabled={(date) =>
                            isBefore(date, form.effectiveFromDate || selectedDate)
                          }
                          className="bg-white"
                        />
                        {form.effectiveToDate && (
                          <div className="p-2 border-t">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full text-xs"
                              onClick={() =>
                                setForm((prev) => ({
                                  ...prev,
                                  effectiveToDate: undefined,
                                }))
                              }
                            >
                              Clear end date
                            </Button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2 pt-1">
              <Label className="text-xs text-palette-jungleGreen600">Excluded dates</Label>
              <p className="text-[11px] text-palette-jungleGreen500">
                No route instances on these days (e.g. bank holidays).
              </p>
              <div className="flex flex-wrap gap-2">
                {(form.excludedDates ?? [])
                  .filter((d) => !isBefore(startOfDay(d), startOfDay(new Date())))
                  .map((d) => {
                    const isToday =
                      startOfDay(d).getTime() === startOfDay(new Date()).getTime()
                    return (
                      <div
                        key={d.getTime()}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-palette-jungleGreen200 text-xs text-palette-jungleGreen700"
                      >
                        {formatDayMonthYear(d)}
                        {!isToday && (
                          <button
                            type="button"
                            className="p-0.5 rounded hover:bg-palette-jungleGreen100 text-palette-jungleGreen400 hover:text-palette-jungleGreen600"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                excludedDates: (prev.excludedDates ?? []).filter(
                                  (x) => x.getTime() !== d.getTime(),
                                ),
                              }))
                            }
                            aria-label="Remove date"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    )
                  })}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs bg-white border-palette-jungleGreen200"
                    >
                      <CalendarIcon size={12} className="mr-1" />
                      Add date
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent variant="plain" align="start">
                    <Calendar
                      mode="single"
                      onSelect={(date) => {
                        if (!date) return
                        const already = (form.excludedDates ?? []).some(
                          (x) => format(x, "yyyy-MM-dd") === format(date, "yyyy-MM-dd"),
                        )
                        if (!already) {
                          setForm((prev) => ({
                            ...prev,
                            excludedDates: [...(prev.excludedDates ?? []), date].sort(
                              (a, b) => a.getTime() - b.getTime(),
                            ),
                          }))
                        }
                      }}
                      className="bg-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </section>

          {children}
          {!isCreatingNewRoute && route?.id && (
            <PlanLineageSection plannedRouteId={route.id} />
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              !form.name ||
              !form.operator ||
              !form.vehicleType ||
              !!routeCodeClash ||
              isSaving
            }
            variant="confirm"
          >
            {isSaving ? (
              <>
                <Loader2 size={14} className="animate-spin mr-1.5" />
                Saving...
              </>
            ) : isCreatingNewRoute ? (
              "Create Route"
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
